import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import SearchableSelect from '../../components/SearchableSelect';
import ExportModal from '../../components/ExportModal';
import { Button } from '../../components/ui';
import './AttendanceReportsPage.css';
import { translateError } from '../../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface MonthlyAttendance {
  totalEvents: number;
  attended: number;
  notAttended: number;
  maybe: number;
  attendanceRate: number;
}

interface MemberAttendanceStats {
  memberId: string;
  memberName: string;
  groups: { _id: string; name: string }[];
  totalEvents: number;
  attended: number;
  notAttended: number;
  maybe: number;
  attendanceRate: number;
  monthly?: Record<string, MonthlyAttendance>;
}

interface AttendanceResponse {
  dateRange: { start: string; end: string };
  summary: {
    totalMembers: number;
    totalEvents: number;
    averageAttendanceRate: number;
  };
  members: MemberAttendanceStats[];
}

interface GroupOption {
  _id: string;
  name: string;
}

interface MemberOption {
  _id: string;
  name: string;
}

// =============================================================================
// HELPERS
// =============================================================================

const MONTH_KEYS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getRateClass(rate: number): string {
  if (rate >= 80) return 'attendance-reports-page__rate--high';
  if (rate >= 50) return 'attendance-reports-page__rate--medium';
  return 'attendance-reports-page__rate--low';
}

// Get month keys present in the data
function getActiveMonths(members: MemberAttendanceStats[]): string[] {
  const monthSet = new Set<string>();
  for (const member of members) {
    if (member.monthly) {
      for (const key of Object.keys(member.monthly)) {
        monthSet.add(key);
      }
    }
  }
  return Array.from(monthSet).sort();
}

function monthKeyToLabel(monthKey: string, t: (key: string) => string): string {
  const parts = monthKey.split('-');
  const monthIndex = parseInt(parts[1], 10) - 1;
  if (monthIndex >= 0 && monthIndex < 12) {
    return t(`statistics.monthsShort.${MONTH_KEYS[monthIndex]}`);
  }
  return monthKey;
}

// =============================================================================
// COMPONENT
// =============================================================================

const AttendanceReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  // Filters
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const [startDate, setStartDate] = useState(formatDateForInput(yearStart));
  const [endDate, setEndDate] = useState(formatDateForInput(now));
  const [groupFilter, setGroupFilter] = useState('');
  const [memberFilter, setMemberFilter] = useState('');

  // Data
  const [data, setData] = useState<AttendanceResponse | null>(null);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const canView = hasPermission('statistics:read');

  // Fetch filter options
  useEffect(() => {
    if (!canView) return;
    const fetchOptions = async () => {
      try {
        const [groupsRes, membersRes] = await Promise.all([
          api.groups.list(),
          api.members.list(),
        ]);
        setGroups(groupsRes.data as GroupOption[]);
        setMembers(membersRes.data as MemberOption[]);
      } catch {
        // Silently fail for filter options
      }
    };
    fetchOptions();
  }, [canView]);

  // Fetch attendance data
  const fetchData = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (startDate) params.startDate = new Date(startDate).toISOString();
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.endDate = end.toISOString();
      }
      if (groupFilter) params.groupId = groupFilter;
      if (memberFilter) params.memberId = memberFilter;

      const { data: result } = await api.statistics.getAttendance(params);
      setData(result as AttendanceResponse);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [canView, startDate, endDate, groupFilter, memberFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Active months in current data
  const activeMonths = useMemo(() => {
    if (!data) return [];
    return getActiveMonths(data.members);
  }, [data]);

  // Group filter options for SearchableSelect
  const groupOptions = useMemo(
    () => groups.map(g => ({ _id: g._id, name: g.name })),
    [groups]
  );

  // Member filter options for SearchableSelect
  const memberOptions = useMemo(
    () => members.map(m => ({ _id: m._id, name: m.name })),
    [members]
  );

  // Export fields
  const exportFields = useMemo(() => [
    { key: 'memberName', label: t('attendanceReport.member'), category: 'attendance' as const, defaultChecked: true },
    { key: 'groups', label: t('attendanceReport.groups'), category: 'attendance' as const, defaultChecked: true },
    { key: 'totalEvents', label: t('attendanceReport.total'), category: 'attendance' as const, defaultChecked: true },
    { key: 'attended', label: t('attendanceReport.attended'), category: 'attendance' as const, defaultChecked: true },
    { key: 'notAttended', label: t('attendanceReport.notAttended'), category: 'attendance' as const, defaultChecked: true },
    { key: 'maybe', label: t('attendanceReport.maybe'), category: 'attendance' as const, defaultChecked: true },
    { key: 'attendanceRate', label: t('attendanceReport.rate'), category: 'attendance' as const, defaultChecked: true },
  ], [t]);

  // Export data
  const exportData = useMemo(() => {
    if (!data) return [];
    return data.members.map(m => ({
      memberName: m.memberName,
      groups: m.groups.map(g => g.name).join(', '),
      totalEvents: m.totalEvents,
      attended: m.attended,
      notAttended: m.notAttended,
      maybe: m.maybe,
      attendanceRate: `${m.attendanceRate}%`,
    }));
  }, [data]);

  if (!canView) {
    return (
      <div className="attendance-reports-page">
        <h2>{t('attendanceReport.title')}</h2>
        <p className="error-message">{t('common.noPermission')}</p>
      </div>
    );
  }

  return (
    <div className="attendance-reports-page">
      {/* Header */}
      <div className="attendance-reports-page__header">
        <h2>{t('attendanceReport.title')}</h2>
        <div className="attendance-reports-page__header-actions">
          <Button
            variant="secondary"
            style={{ background: 'var(--bg-light)' }}
            onClick={() => setShowExportModal(true)}
            disabled={loading || !data}
          >
            {t('export.exportButton')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="attendance-reports-page__filters">
        <div className="attendance-reports-page__filter-group">
          <label>{t('attendanceReport.startDate')}</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="attendance-reports-page__filter-group">
          <label>{t('attendanceReport.endDate')}</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="attendance-reports-page__filter-group">
          <label>{t('attendanceReport.filterByGroup')}</label>
          <SearchableSelect
            options={groupOptions}
            value={groupFilter || null}
            onChange={(val) => setGroupFilter(val == null ? '' : String(val))}
            placeholder={t('attendanceReport.allGroups')}
          />
        </div>
        <div className="attendance-reports-page__filter-group">
          <label>{t('attendanceReport.filterByMember')}</label>
          <SearchableSelect
            options={memberOptions}
            value={memberFilter || null}
            onChange={(val) => setMemberFilter(val == null ? '' : String(val))}
            placeholder={t('attendanceReport.allMembers')}
          />
        </div>
      </div>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      {loading ? (
        <div className="attendance-reports-page__loading">{t('attendanceReport.loading')}</div>
      ) : data && (
        <>
          {/* Summary Cards */}
          <div className="attendance-reports-page__summary">
            <div className="attendance-reports-page__summary-card">
              <div className="attendance-reports-page__summary-card-label">
                {t('attendanceReport.totalMembers')}
              </div>
              <div className="attendance-reports-page__summary-card-value">
                {data.summary.totalMembers}
              </div>
            </div>
            <div className="attendance-reports-page__summary-card">
              <div className="attendance-reports-page__summary-card-label">
                {t('attendanceReport.totalEvents')}
              </div>
              <div className="attendance-reports-page__summary-card-value">
                {data.summary.totalEvents}
              </div>
            </div>
            <div className="attendance-reports-page__summary-card">
              <div className="attendance-reports-page__summary-card-label">
                {t('attendanceReport.averageAttendance')}
              </div>
              <div className="attendance-reports-page__summary-card-value attendance-reports-page__summary-card-value--highlight">
                {data.summary.averageAttendanceRate}%
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="attendance-reports-page__table-container">
            <table className="attendance-reports-page__table">
              <thead>
                <tr>
                  <th className="attendance-reports-page__col-member">{t('attendanceReport.member')}</th>
                  <th className="attendance-reports-page__col-groups">{t('attendanceReport.groups')}</th>
                  <th>{t('attendanceReport.total')}</th>
                  <th>{t('attendanceReport.attended')}</th>
                  <th>{t('attendanceReport.notAttended')}</th>
                  <th>{t('attendanceReport.maybe')}</th>
                  <th>{t('attendanceReport.rate')}</th>
                  {activeMonths.map((mk) => (
                    <th key={mk} className="attendance-reports-page__col-month">
                      {monthKeyToLabel(mk, t)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.members.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7 + activeMonths.length}
                      className="attendance-reports-page__no-data"
                    >
                      {t('attendanceReport.noData')}
                    </td>
                  </tr>
                ) : (
                  data.members.map((member) => (
                    <tr key={member.memberId}>
                      <td className="attendance-reports-page__col-member">{member.memberName}</td>
                      <td className="attendance-reports-page__col-groups">
                        {member.groups.map(g => g.name).join(', ') || '-'}
                      </td>
                      <td>{member.totalEvents}</td>
                      <td>{member.attended}</td>
                      <td>{member.notAttended}</td>
                      <td>{member.maybe}</td>
                      <td className={`attendance-reports-page__col-rate ${getRateClass(member.attendanceRate)}`}>
                        {member.attendanceRate}%
                      </td>
                      {activeMonths.map((mk) => {
                        const monthly = member.monthly?.[mk];
                        if (!monthly || monthly.totalEvents === 0) {
                          return <td key={mk} className="attendance-reports-page__col-month">-</td>;
                        }
                        return (
                          <td
                            key={mk}
                            className={`attendance-reports-page__col-month ${getRateClass(monthly.attendanceRate)}`}
                          >
                            {monthly.attendanceRate}%
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        filename={`attendance-report-${startDate}-${endDate}`}
        availableFields={exportFields}
        title={t('attendanceReport.title')}
      />
    </div>
  );
};

export default AttendanceReportsPage;
